import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { QuoteFilterOptions } from '~/lib/quotes';
import { BasketQuote } from '~/lib/types/basket-types';
import { useMemo } from 'react';
import { onlyUniqueNonBlank } from '~/lib/utils/array';
import Filter from './quote-filter-option';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import {
  generateEstimatedDeliveryGroupLabel,
  generateEstimatedDeliveryGroups
} from './estimated-delivery-group-helper';
import QuoteFilterSection from './quote-filter-section';
import { P2GProgress } from '@parcel2go/core-ui';

export default function QuoteFilterDeliveryOptions({
  filter,
  onChange,
  title,
  dataTestId,
  destination,
  origin,
  quotes
}: {
  title?: string;
  dataTestId?: string;
  origin?: string;
  destination?: string;
  filter: QuoteFilterOptions;
  filteredQuotes: BasketQuote[];
  quotes: BasketQuote[];
  onChange: (filter: QuoteFilterOptions) => void;
}) {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const { settings } = useTenant();

  const isDomestic =
    settings[ConfigSetting.DEFAULT_DESTINATION_COUNTRY] === destination &&
    settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY] === origin;

  const optionGroups = useMemo(() => generateEstimatedDeliveryGroups(quotes, isDomestic), [quotes, isDomestic]);

  if (optionGroups.length === 0) {
    return (
      <QuoteFilterSection title={title} dataTestId={dataTestId}>
        <P2GProgress />
      </QuoteFilterSection>
    );
  }

  return (
    <Filter
      dataTestId={dataTestId}
      options={optionGroups.map((x) => ({
        key: x.type,
        label: t(x.translationKey, { defaultValue: generateEstimatedDeliveryGroupLabel(x.translationKey) }),
        type: x.type
      }))}
      selected={optionGroups
        .filter((x) => x.days?.length && x.days.every((d) => filter.estimatedDeliveryDays?.includes(d)))
        .map((x) => x.type)}
      title={title}
      onChange={(v) => {
        const days = optionGroups.find((o) => o.type === v)?.days || [];

        const isSelected =
          !!days.length &&
          !!filter?.estimatedDeliveryDays?.length &&
          days.every((d) => filter.estimatedDeliveryDays?.includes(d));

        const updated = isSelected
          ? (filter?.estimatedDeliveryDays || []).filter((x) => !days.includes(x))
          : (filter?.estimatedDeliveryDays || []).concat(days);

        onChange({
          ...filter,
          estimatedDeliveryDays: updated.filter(onlyUniqueNonBlank)
        });
      }}
    />
  );
}
